/**
 * Responsibility: the discovery board data, per tab.
 *
 * Keyed on era and floor together, because those two select the view and
 * switching between them should be instant once each has been seen. The hub
 * computes every floor from one screening pass, so a second request for a
 * different floor is cheap on its side too.
 *
 * No polling. The universe is rebuilt weekly upstream and prices daily, so a
 * background refetch would ask a free service for a file it has not changed.
 * The refresh button is the only thing that re-polls.
 *
 * Every mutation returns the refilled board or archive, so each one seeds the
 * cache directly. That matters more here than elsewhere: binning a card
 * promotes another into its place, and a client that merged rows itself would
 * be a second copy of the ranking rules.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hubApi } from '../client';
import type {
  ArchiveIntent,
  CardEra,
  CardLanguage,
  DiscoveryArchive,
  DiscoveryBoard,
} from '../client';

const boardKey = (era: CardEra, floor: number, language: CardLanguage) =>
  ['grading', 'discover', era, floor, language] as const;
const archiveKey = (era: CardEra, intent: ArchiveIntent) =>
  ['grading', 'discover', 'archive', era, intent] as const;

export function useDiscoveryBoard(era: CardEra, floor: number, language: CardLanguage) {
  return useQuery({
    queryKey: boardKey(era, floor, language),
    queryFn: () => hubApi.discoveryBoard(era, floor, language),
    refetchOnWindowFocus: false,
    // A board is a slice of a cached universe, so it stays good until
    // something is binned or the operator refreshes.
    staleTime: 5 * 60_000,
  });
}

export function useDiscoveryArchive(era: CardEra, intent: ArchiveIntent = 'BINNED') {
  return useQuery({
    queryKey: archiveKey(era, intent),
    queryFn: () => hubApi.discoveryArchive(era, intent),
    refetchOnWindowFocus: false,
  });
}

/**
 * Sold prices and per-card links, loaded when a detail dialog opens.
 *
 * Costs one request to BetterPop per card, so it is deliberately not part of
 * the board. Cached indefinitely for the session: completed sales do not
 * change while a dialog is open, and re-opening the same card should not
 * cost another call.
 */
/** Shared with the prefetch queue, so a warmed card is the one read. */
export const cardDetailKey = (discoveryKey: string) =>
  ['grading', 'discover', 'card', discoveryKey] as const;

export function useCardDetail(discoveryKey: string | null) {
  return useQuery({
    queryKey: cardDetailKey(discoveryKey ?? ''),
    queryFn: () => hubApi.discoveryCard(discoveryKey as string),
    enabled: discoveryKey !== null,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

export function useArchiveCard(era: CardEra, floor: number, language: CardLanguage) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ discoveryKey, intent }: { discoveryKey: string; intent: ArchiveIntent }) =>
      hubApi.archiveDiscovered(era, floor, language, discoveryKey, intent),
    onSuccess: (board: DiscoveryBoard) => {
      client.setQueryData(boardKey(era, floor, language), board);
      // The other floors now hold a card that is no longer on any board, and
      // the archive tab has gained a row.
      client.invalidateQueries({ queryKey: ['grading', 'discover'] });
    },
  });
}

export function useRestoreCard(era: CardEra) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (discoveryKey: string) => hubApi.restoreDiscovered(era, discoveryKey),
    onSuccess: (archive: DiscoveryArchive) => {
      // Restoring can only come from a tab, and the response is that tab.
      client.setQueryData(archiveKey(era, archive.intent), archive);
      // The card rejoins the boards wherever it ranks.
      client.invalidateQueries({ queryKey: ['grading', 'discover'] });
    },
  });
}

export function useRefreshDiscovery(era: CardEra, floor: number, language: CardLanguage) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => hubApi.refreshDiscovery(era, floor, language),
    onSuccess: (board: DiscoveryBoard) => {
      client.setQueryData(boardKey(era, floor, language), board);
      client.invalidateQueries({ queryKey: ['grading', 'discover'] });
    },
  });
}
